import React, {forwardRef, useRef, useState} from 'react';
import './styles.css'
import EyeIcon from "../../../assets/icons/EyeIcon";
import EyeOffIcon from "../../../assets/icons/EyeOffIcon";

let nextInputId = 0;

const Input = forwardRef(({
                              min,
                              max,
                              maxLength,
                              minLength,
                              name,
                              id,
                              label,
                              required = false,
                              type = 'text',
                              value,
                              onChange,
                              onKeyDown,
                              placeholder = '',
                              style = {},
                              className = '',
                              disabled = false,
                              isTextarea,
                              resize,
                              onBlur = () => {
                              },
                              defaultValue,
                              validate,
                              readOnly = false,
                              variant = 'default', // flat
                              leadingIcon = null,
                              trailingIcon = null,
                              ...rest
                          }, ref) => {

    /*
     * Invalid is a class rather than an inline border. Inline it could only ever
     * say one thing - a red line - and it outranked every rule in the
     * stylesheet, so the ring, the text colour and the focus state of an invalid
     * field had nowhere to live.
     */
    const [isValid, setIsValid] = useState(true);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const generatedId = useRef(null);
    if (generatedId.current === null) generatedId.current = `standard-input-${++nextInputId}`;
    const inputId = id || (label ? generatedId.current : undefined);
    const isPassword = type === 'password';
    const hasFormElementClass = className.split(' ').includes('form-element');

    const handleBlur = (e) => {
        let valid = true;
        if (validate) {
            try {
                validate(e.target.value);
                valid = true;
            } catch (err) {
                valid = false;
            }
        }
        setIsValid(valid);

        if (typeof onBlur === 'function' && valid) {
            onBlur(e);
        }
    };

    const textarea = isTextarea ? (
        <textarea
            ref={ref}
            id={inputId}
            required={required}
            defaultValue={defaultValue}
            maxLength={maxLength}
            minLength={minLength}
            name={name}
            value={value}
            placeholder={placeholder}
            style={{
                ...style,
                resize: resize,
            }}
            className={`standard-input ${className} ${variant} ${validate && !isValid ? 'is-invalid' : ''}`}
            onBlur={handleBlur}
            onChange={onChange}
            onKeyDown={onKeyDown}
            disabled={disabled}
            {...rest}
        />
    ) : null;

    const input = !isTextarea ? (
        <input
            ref={ref}
            id={inputId}
            required={required}
            defaultValue={defaultValue}
            min={min}
            max={max}
            maxLength={maxLength}
            minLength={minLength}
            name={name}
            type={isPassword && passwordVisible ? 'text' : type}
            value={value}
            placeholder={placeholder}
            style={style}
            className={`standard-input ${className} ${variant} ${leadingIcon ? 'has-leading-icon' : ''} ${(trailingIcon || isPassword) ? 'has-trailing-icon' : ''} ${validate && !isValid ? 'is-invalid' : ''}`}
            onBlur={handleBlur}
            onChange={onChange}
            onKeyDown={onKeyDown}
            disabled={disabled}
            readOnly={readOnly}
            {...rest}
        />
    ) : null;

    const control = isTextarea ? textarea : (!isPassword && !leadingIcon && !trailingIcon ? input : (
        <div className={`standard-input-wrapper ${isPassword ? 'standard-password-input-wrapper' : ''} ${hasFormElementClass ? 'has-form-element' : ''}`}>
            {leadingIcon && (
                <span className="standard-input-icon standard-input-icon--leading" aria-hidden="true">
                    {leadingIcon}
                </span>
            )}
            {input}
            {trailingIcon && !isPassword && (
                <span className="standard-input-icon standard-input-icon--trailing" aria-hidden="true">
                    {trailingIcon}
                </span>
            )}
            {isPassword && (
                <button
                    type="button"
                    className="standard-password-toggle"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    onMouseDown={(event) => event.preventDefault()}
                    disabled={disabled || readOnly}
                    aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                >
                    {passwordVisible ? (
                        <EyeOffIcon width="20" height="20" strokeWidth="1.8"/>
                    ) : (
                        <EyeIcon width="20" height="20" strokeWidth="1.8"/>
                    )}
                </button>
            )}
        </div>
    ));

    if (!label) return control;

    return (
        <div className="standard-input-field">
            <label className="standard-input-label" htmlFor={inputId}>
                {label}{required && <span className="standard-input-required" aria-hidden="true"> *</span>}
            </label>
            {control}
        </div>
    );
});

export default Input;



// WEBPACK FOOTER //
// ./src/components-new/form/Input/index.jsx